/**
 * Reconnaissance des erreurs PostgREST / Postgres « objet absent » : utile quand le front est
 * déployé avant les migrations (colonne, table ou RPC pas encore créées). L'appelant se dégrade
 * alors proprement (fonction masquée, valeur par défaut) au lieu d'afficher une erreur ou de boucler.
 */

interface ErrorLike {
  code?: unknown;
  message?: unknown;
  details?: unknown;
  hint?: unknown;
}

function parts(error: unknown): { code: string; text: string } {
  if (!error || typeof error !== "object") return { code: "", text: typeof error === "string" ? error : "" };
  const err = error as ErrorLike;
  const text = [err.message, err.details, err.hint].filter((v): v is string => typeof v === "string").join(" ");
  return { code: typeof err.code === "string" ? err.code : "", text };
}

/** Message générique affiché quand une fonction attend une migration pas encore appliquée. */
export const SCHEMA_PENDING_MESSAGE =
  "Fonction pas encore disponible : la mise à jour de la base de données est en attente.";

/**
 * Colonne absente : 42703 (Postgres, select) ou PGRST204 (cache de schéma PostgREST), ou message
 * « column … does not exist ». Avec `column`, le message doit la citer quand il est disponible.
 */
export function isMissingColumnError(error: unknown, column?: string): boolean {
  const { code, text } = parts(error);
  const byCode = code === "42703" || code === "PGRST204";
  const byText = /column\s+\S+\s+does not exist/i.test(text) || /could not find the '[^']+' column/i.test(text);
  if (!byCode && !byText) return false;
  if (!column || !text) return true;
  return text.includes(column);
}

/** Table / vue absente : 42P01 (Postgres) ou PGRST205 (PostgREST), ou message équivalent. */
export function isMissingRelationError(error: unknown): boolean {
  const { code, text } = parts(error);
  return (
    code === "42P01" ||
    code === "PGRST205" ||
    /relation\s+\S+\s+does not exist/i.test(text) ||
    /could not find the table/i.test(text)
  );
}

/** Fonction (RPC) absente : PGRST202 (PostgREST) ou 42883 (Postgres), ou message équivalent. */
export function isMissingFunctionError(error: unknown): boolean {
  const { code, text } = parts(error);
  return (
    code === "PGRST202" ||
    code === "42883" ||
    /could not find the function/i.test(text) ||
    /function\s+\S+\s+does not exist/i.test(text)
  );
}

/** Colonne, table ou RPC absente (migration pas encore appliquée). */
export function isMissingSchemaError(error: unknown): boolean {
  return isMissingColumnError(error) || isMissingRelationError(error) || isMissingFunctionError(error);
}
